<template>
    <b-container fluid="" class="chart-item-container no-padding-mobile px-0">
        {{ /* Icon, title and value/metric */ }}
        <b-row>
            <b-col cols="12" lg="7" xl="9" class="d-flex justify-content-between align-items-center mb-2 mb-lg-3">
                <span class="title-text-wrapper">
                    <span class="title-icon-wrapper"> <SvgIcon :name="icon"/></span>
                    <span class="graphics-item-title  ml-2">{{ title }}</span>
                    <div
                        v-if="notInIgnoreList"
                        class="ml-2 graphics-item-measurement d-inline"
                    >
                        <template v-if="isLevelmeterType">
                            <span class="ml-1">
                                ({{ combineMeasurementUnit(measurement[0], 'units.meters') }},
                            </span>
                            <span class="ml-1">
                                {{ $t('sensors-page.spillway') }}: {{ highestWorkingWaterLevel }} {{ $t('units.meters') }},
                            </span>
                            <span class="ml-1">
                                {{ combineMeasurementUnit(measurement[1], metric) }})
                            </span>
                        </template>
                        <template v-else-if="isPrecipitationType">
                            {{ (getPrecipitationMeasurements(measurement)) }}
                        </template>
                        <span v-else>({{ combineMeasurementUnit(measurement, metric) }})</span>
                    </div>
                </span>
            </b-col>
            {{ /* Zoom / home icons */ }}
            <b-col cols="12" lg="5" xl="3" class="d-flex justify-content-start justify-content-lg-end align-items-center mb-0 mb-lg-3">
                    <WhiteIconButton
                        v-if="isZoomShown"
                        icon="zoom-search"
                        :tooltip="$t('sensors-page.reset-zoom')"
                        :emit-this="'reset-zoom'"
                        v-on:reset-zoom="changeZoomKey"
                        class="d-inline-block remove-margin"
                    ></WhiteIconButton>
                    <!-- <WhiteIconButton
                        icon="move"
                        :tooltip="$t('sensors-page.change-size')"
                        :emit-this="'toggle-container-size'"
                        v-on:toggle-container-size="toggleContainerSize"
                        class="d-inline-block remove-margin"
                    ></WhiteIconButton> -->
            </b-col>
        </b-row>
        {{ /* Chart */ }}
        <b-row no-gutters class="border-light-grey" align-v="stretch">
            <b-col cols="12" class="pt-1 pt-sm-3 pb-1 pb-sm-3">
                <b-overlay :show="loading" spinner-variant="primary">
                    <ChartBooleanList
                        v-if="!loading && !error && chartData.series.length > 0"
                        :chart-data="chartData"
                        :zoom-key="zoomKey"
                        :resize-key="resizeKey"
                        :reverse="reverse"
                        :type-chart="typeChart"
                        :metric="metrics"
                        :show-plot-line="showPlotLine"
                    >
                    </ChartBooleanList>
                    <div
                        v-else-if="init && !loading && !error"
                        class="choose-period-message"
                    >
                        {{ $t("sensors-page.choose-period") }}
                    </div>
                    <div
                        v-else-if="!loading && !error && chartData.series.length === 0"
                        class="choose-period-message"
                    >
                        {{ $t("sensors-page.no-data") }}
                    </div>
                    <div v-else-if="!loading && error" class="get-chart-error-message">
                        {{ $t("sensors-page.error-chart-fetch") }}
                    </div>
                </b-overlay>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
    // mixins
    import chartSafeTooltipMixin from "../../mixins/chartSafeTooltipMixin";
    import ChartBaseItemMixin from "../../mixins/ChartBaseItemMixin";
    // utils
    import { getKey } from "../../utils/common";
    // constants
    import {autoStartTimeFrame} from "../../constants/sensors-constants";
    // components
    import WhiteIconButton from "../shared/WhiteIconButton";
    import ChartBooleanList from "./ChartBooleanList";
    import SvgIcon from "../shared/SvgIcon";

    /**
     * Container for the actual chart.
     * Fetches the data, keeps state - date, error.
     * Emits resize event and uses the child's
     * zoom method if nay.
     */

    export default {
        name: "ChartItem",
        mixins: [chartSafeTooltipMixin, ChartBaseItemMixin],
        components: {
            WhiteIconButton,
            ChartBooleanList,
            SvgIcon,
        },
        props: {
            icon: String,
            title: String,
            psObjectName: String,
            measurement: [String, Number, Array],
            metric: String,
            alternative: Boolean,
            hasMultiDates: {
                type: Boolean,
                default: false,
            },
            autoStart: {
                type: Boolean,
                default: false
            },
            autoStartTimeFrame: {
                type: Number,
                default: autoStartTimeFrame
            },
            sensorData: {
                type: Number
            },
            showTripleData: {
                type: Boolean,
                default: false
            },
            showPlotLine: {
                type: Boolean,
                default: false
            },
            measuringProfile: {
                type: Number,
                default: 0
            }
        },
        data() {
            /**
             * init: if pristine
             * loading: status - if waiting for data from DB
             * error: show error on failed fetch
             * showStatistics: hardcoded, always by default
             * zoomKey: watched in chart wrapper; will call inner chart as to apply zoom reset
             * resizeKey: dynamic number to force re-render the component (hack)
             * dateRange: passed to Picker, send to BE
             * dataStyle: pre-defined styles for graphics
             * chartData: includes placeholders for labels and datasets, also static plot lines
             * typeChart: area, rose, spline, reverse-spline
             * reverse:   if chart has reverse X and Y axis
             */
            return {
                init: true,
                loading: false,
                error: false,
                showStatistics: true,
                zoomKey: getKey(),
                resizeKey: getKey(),
                chartData: {
                    series: [],
                    plotLines: {},
                },
                typeChart: null,
                reverse: false
            };
        },
        updated() {
            // console.log(this.chartData)
        },
    };
</script>

<style lang="scss" scoped>
    @import "../../assets/scss/variables";
    @import "../../assets/scss/mixins";

    .remove-margin {
        margin: 0 !important;
        padding: 0 !important;

        &:nth-child(1) {
            margin-right: 12px !important;
        }
    }

    .btn-wrapper {
        width: 50%;
        margin: 10px 0;

        @include media-breakpoint-up(xl) {
            width: auto;
            margin: 0;

            &:last-child {
                margin-right: 0;
            }
        }
    }
</style>
